import { useTheme } from "@mui/material";
import { ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { Swiper as SwiperType } from "swiper";
import { CarouselStyle } from "./CarouselWidget.styles";
import { ButtonDigited, StyledTypography } from "../../../components/Button/Button.variants";
import { useSweepState } from "../../../hooks";
import { PaginationCarousel } from "../../PaginationCarousel/PaginationCarousel.component";
import { Carousel } from "../Carousel.component";

type Items = {
  items: {
    corporateRedactionalActivityObjects: WidgetElement[]
  }
}

export type WidgetElement = {
  title: string,
  slide: JSX.Element,
  coverPublicURL: string,
  externalFileUrl: string,
  isLinkRelative?: boolean, //true indicates link, false indicates external link
  id: number
}


export const CarouselWidget = ({ items }: Items) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const {
    autoSweep,
    handleAutoSweep,
    swiperInstance,
    setSwiperInstance,
    activeIndex,
    setActiveIndex
  } = useSweepState();

  const isInternalLink = () => items?.corporateRedactionalActivityObjects[activeIndex].isLinkRelative;

  function handleRedirectUrl() {
    const url = items?.corporateRedactionalActivityObjects[activeIndex].externalFileUrl;
    const id = items?.corporateRedactionalActivityObjects[activeIndex].id;

    if (isInternalLink()) {
      const regex = /\/$/; //check if url has / at the end

      if(regex.test(url)){
        return `${url}${id}`; //link url and id without / in between

      }
      return `${url}/${id}`; //link url and id with / in between
    }
    return `${url}`;
  }

  return (
    <CarouselStyle className="carouselContainer">
      <PaginationCarousel
        idScope={ "carousel-widget" }
        autoSweep={ autoSweep }
        handleAutoSweep={ handleAutoSweep }
        swiperInstance={ swiperInstance }
        activeIndex={ activeIndex }
        onSetActiveIndex={ () => setActiveIndex }
        items={ items?.corporateRedactionalActivityObjects || [] }
      />

      <Carousel
        carouselData={ items?.corporateRedactionalActivityObjects || [] }
        onSwipeSetIstance={ (event: SwiperType) => {
          setActiveIndex(event.activeIndex);
          setSwiperInstance(event);
        }
        }
        handleRedirectUrl={ () => handleRedirectUrl() }
        isLinkRelative={ isInternalLink() }
      />
      <ButtonDigited typology="secondary" buttonSize="sm" selfPosition="flex-end" sx={ { marginTop: "1rem" } }>
        <a
          style={ {
            color:theme.customColors.textPrimary,
            gap: "0.5rem",
            fontWeight: 500,
            textDecoration: "none"
          } }
          href={ handleRedirectUrl() }
          target={ isInternalLink() ? "_self" : "_blank" }
          rel={ isInternalLink() ? undefined : "noopener noreferrer" }
          aria-label={ t("discover_more") }
        >
          <StyledTypography>
            { t("discover_more") }{ " " }
          </StyledTypography>
          <ChevronRight size="0.875rem" />
        </a>
      </ButtonDigited>
    </CarouselStyle>
  );
};
